import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../hook/useAppDispatch';
import { useAppSelector } from '../../hook/useAppSelector';
import Table from '../ContentWrapper/Common/Table/Table';
import FallbackLoader from '../ContentWrapper/Content/Loader/FallbackLoader';
import { applySearchInput, fetchStats } from '../../store/slice/stats-slice';
import { StatsFetchType } from '../../enum/StatsFetchType';
import { setShowColumnOrder } from '../../store/slice/global-filters';

import { SEARCH_INPUT_DEBOUNCE_DELAY } from '../../constant/constants';
import { AgmsResponseCode } from '../../enum/AgmsResponseCode';
import { setColumns } from '../../store/slice/column-slice';
import { useStatsPayload } from '../../hook/useStatsPayload';

export const CHUNK_SIZE = 50;
export const CHUNK_SCROLL_VISIBLE_SIZE = 3;
export const LOADER_DISPLAY_DELAY_MS = 700;

const StatisticsPage: React.FC = () => {
  const statsPayload = useStatsPayload();
  const statsData = useAppSelector((state) => state.data.statsData);
  const dispatch = useAppDispatch();

  const { filteredData } = useAppSelector((state) => state.data);
  const loading = useAppSelector((state) => state.data.loading);
  const selectedCurrency = useAppSelector((state) => state.stats.selectedCurrency);
  const searchInput = useAppSelector((state) => state.deviceList.searchInput);

  const [nextChunk, setNextChunk] = useState(CHUNK_SCROLL_VISIBLE_SIZE);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(nextChunk * CHUNK_SIZE);
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const timeout: NodeJS.Timeout | null = null;

    let startIndexTemp = 0;
    let endIndexTemp = nextChunk * CHUNK_SIZE;

    if (filteredData.Items.length > CHUNK_SIZE * CHUNK_SCROLL_VISIBLE_SIZE) {
      setShowLoader(true);

      if (nextChunk > CHUNK_SCROLL_VISIBLE_SIZE) {
        startIndexTemp = (nextChunk - CHUNK_SCROLL_VISIBLE_SIZE) * CHUNK_SIZE;
        endIndexTemp = nextChunk * CHUNK_SIZE;
      }

      if (endIndexTemp > filteredData.Items.length) {
        endIndexTemp = filteredData.Items.length;
      }

      setTimeout(() => {
        setShowLoader(false);
      }, LOADER_DISPLAY_DELAY_MS);

      setStartIndex(startIndexTemp);
      setEndIndex(endIndexTemp);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [nextChunk, filteredData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== null) {
        setNextChunk(CHUNK_SCROLL_VISIBLE_SIZE);
        dispatch(applySearchInput(searchInput));
      }
    }, SEARCH_INPUT_DEBOUNCE_DELAY);

    return () => clearTimeout(timeout);
  }, [dispatch, searchInput]);

  const onNearBottom = () => {
    setNextChunk((prev) => prev + 1);
  };

  const onNearTop = () => {
    setNextChunk((prev) => (prev > 2 ? prev - 2 : prev));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchStats({
            type: StatsFetchType.STATS,
            request: { ...statsPayload, CCY: selectedCurrency },
          }),
        );
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };

    fetchData();
    dispatch(setShowColumnOrder(true));
  }, [dispatch, selectedCurrency, statsPayload]);

  useEffect(() => {
    if (statsData?.Items?.length > 0) {
      dispatch(setColumns(Object.keys(statsData.Items[0])));
    }
  }, [dispatch, statsData.Items]);

  return (
    <>
      {(loading || showLoader) && <FallbackLoader />}
      <Table
        error={filteredData.ResponseCode !== AgmsResponseCode.OK ? filteredData.Message!! : ''}
        data={filteredData.Items && filteredData.Items.length ? filteredData.Items.slice(startIndex, endIndex) : []}
        dataLength={filteredData.Items.length}
        getCurrentChunkPosition={() => nextChunk}
        showFooter={true}
        onNearBottom={onNearBottom}
        onNearTop={onNearTop}
      />
    </>
  );
};

export default StatisticsPage;
